import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { InitialRender } from "../../common/Helper";
import CopyToClipboard from "../../CopyToClipboard";
import { RenderCustomerStatus } from "../../../utils/CommonStatusItems";
import { RenderLearningMethodGradebook, RenderResourceCampus } from "../../../utils/CommonGroupingItem";
import { findCustomersByMobileEmailStudentNumber } from "../../../services/SoftphoneService";
import useDebounce from "../../../hooks/useDebounce";
import { renderBrand, renderCampus } from "../CallDashboardMappings";

const CustomerBlock = ({ 
    customerData, 
    loading, 
    brandMapping = {},
    campusMapping = {},
    opportunityData = [],
    onCustomerSelect, 
    onReturnToSearch,
    initialMatches = [] 
}) => {
    const [search, setSearch] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [noRecordsFound, setNoRecordsFound] = useState(false);
    
    // Use debounced search term
    const debouncedSearch = useDebounce(debouncedTerm, 1000);
    
    // Use a ref to track if the search was user-initiated
    const userInitiatedSearch = useRef(false);

    // Scroll-position tracking for blur lines (Call Dashboard spec)
    const matchesListRef = useRef(null);
    const [scrollPosition, setScrollPosition] = useState('top'); // 'top' | 'middle' | 'bottom'

    const handleMatchesScroll = () => {
        const el = matchesListRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
        if (atTop && atBottom) {
            setScrollPosition('none'); // no scroll needed
        } else if (atTop) {
            setScrollPosition('top');
        } else if (atBottom) {
            setScrollPosition('bottom');
        } else {
            setScrollPosition('middle');
        }
    };

    // Re-evaluate scroll position when searchResults change
    useEffect(() => {
        handleMatchesScroll();
    }, [searchResults]);

    // Initialize with initialMatches if provided
    useEffect(() => {
        if (initialMatches && initialMatches.length > 0) {
            setSearchResults(initialMatches);
            setSelectedCustomer(initialMatches[0]);
        }
    }, [initialMatches]);

    // Debounced search effect
    useEffect(() => {
        if (debouncedSearch.trim().length >= 5) {
            performSearch(debouncedSearch);
        } else if (debouncedSearch.trim() === '') {
            // If user clears search, and we rely on initial matches, maybe keep them? 
            // Or clear everything?
            // If user clears input, we probably reset to empty unless initial matches exist.
            // For now, let's clear if user clears input.
            if (userInitiatedSearch.current) {
                setSearchResults([]);
                setSelectedCustomer(null);
                setNoRecordsFound(false);
            }
        } else {
            // If search is less than 5 characters, clear results and stop searching
            setIsSearching(false);
            if (userInitiatedSearch.current) {
                setSearchResults([]);
                setSelectedCustomer(null);
                setNoRecordsFound(false);
            }
        }
    }, [debouncedSearch]);

    const performSearch = async (term) => {
        setIsSearching(true);
        setNoRecordsFound(false);
        userInitiatedSearch.current = true;
        
        const source = axios.CancelToken.source();
        try {
            const isEmail = term.includes('@');
            const searchData = isEmail 
                ? { email: term.trim() }
                : { studentNumber: term.trim() };

            const res = await findCustomersByMobileEmailStudentNumber(searchData, source.token);
            
            if (res?.status === 200 && res?.data) {
                const customers = Array.isArray(res.data) ? res.data : [res.data];
                // Filter out empty objects or invalid data
                const validCustomers = customers.filter(c => c && (c.fullName || c.studentNumber || c.customerId));
                
                // Limit results to 10
                const limitedCustomers = validCustomers.slice(0, 10);
                
                if (limitedCustomers.length > 0) {
                    setSearchResults(limitedCustomers);
                    setSelectedCustomer(limitedCustomers[0]);
                } else {
                    setSearchResults([]);
                    setSelectedCustomer(null);
                    setNoRecordsFound(true);
                }
            } else {
                setSearchResults([]);
                setSelectedCustomer(null);
                setNoRecordsFound(true);
            }
        } catch (error) {
            console.error("Search error:", error);
            setSearchResults([]);
            setSelectedCustomer(null);
            setNoRecordsFound(true);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        setDebouncedTerm(value);
    };

    // Helper function to get a unique identifier for a customer
    const getCustomerId = (customer) => {
        if (!customer) return null;
        // Try all possible ID fields
        const id = customer.customerId || customer.studentNumber || customer.id;
        if (id) return String(id).trim();
        // Fallback to email if no ID
        return customer.email ? String(customer.email).trim().toLowerCase() : null;
    };

    // Helper function to check if two customers are the same
    const isSameCustomer = (customer1, customer2) => {
        if (!customer1 || !customer2) return false;
        const id1 = getCustomerId(customer1);
        const id2 = getCustomerId(customer2);
        if (!id1 || !id2) return false;
        return id1 === id2;
    };

    const handleSelect = (customer) => {
        setSelectedCustomer(customer);
    };

    const handleLoadDetails = () => {
        if (selectedCustomer && onCustomerSelect) {
            onCustomerSelect(selectedCustomer);
        }
    };

    // Determine view state
    const hasCustomerData = customerData && customerData.studentNumber;
    
    if (loading) {
        return <div className="customer-card-container"><div className="p-2"><SkeletonTicketList /></div></div>;
    }

    // Wrapper functions to pass brandMapping and campusMapping to the consolidated render functions
    // CustomerBlock needs objects returned, so we use returnObject option
    const renderBrandWithMapping = (brandId) => renderBrand(brandId, brandMapping, { returnObject: true });
    const renderCampusWithMapping = (campusId) => renderCampus(campusId, campusMapping, { returnObject: true });

    if (hasCustomerData) {
        // Single Match / Details View – aligned to Figma node 505:43016
        // Try subsidiary first, then subsidiaryName
        const subsidiaryValue = customerData.subsidiary || customerData.subsidiaryName;
        const brandResult = renderBrandWithMapping(subsidiaryValue);
        const brandHtml = brandResult?.html;
        const brandText = brandResult?.text;
        const learningMethodResult = customerData.deliverymethod ? RenderLearningMethodGradebook(customerData.deliverymethod) : null;
        const learningMethodText = learningMethodResult?.text || "-";
        const learningMethodColor = learningMethodResult?.color || "cat-gray";
        const campusResult = renderCampusWithMapping(customerData.campus);
        const campusText = campusResult?.text || "-";
        const campusColor = campusResult?.color || "cat-gray";
        const campusIcon = campusResult?.icon || "fa-university";
        
        // Get intake from opportunity data (first opportunity)
        const formatIntake = (intake) => {
            if (!intake) return "-";
            const intakeStr = String(intake).trim();
            // Match patterns like "2026 Intake 1" or "2026 Intake 1" (case insensitive)
            const match = intakeStr.match(/(\d{4})\s*Intake\s*(\d+)/i);
            if (match) return `${match[1]}-${match[2]}`;
            return intakeStr; // Return as-is if format doesn't match
        };
        const opportunityIntake = Array.isArray(opportunityData) && opportunityData.length > 0 
            ? formatIntake(opportunityData[0].intake) 
            : null;
        const formattedCustomerIntake = formatIntake(customerData.intake);

        return (
            <div className="customer-card-container">
                {/* Header: Avatar + Name + Student # + Status */}
                <div className="customer-header">
                    <div className="customer-name-block">
                        {customerData.profilePhotoUrl ? (
                            <img 
                                src={customerData.profilePhotoUrl} 
                                alt={customerData.fullName}
                                className="avatar-circle"
                                style={{ width: 40, height: 40, marginRight: 8, objectFit: 'cover' }}
                                onError={(e) => {
                                    // Fallback to initials if image fails to load
                                    e.target.style.display = 'none';
                                    if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                    }
                                }}
                            />
                        ) : null}
                        <div 
                            className="avatar-circle" 
                            style={{ 
                                width: 40, 
                                height: 40, 
                                fontSize: 16, 
                                marginRight: 8,
                                display: customerData.profilePhotoUrl ? 'none' : 'flex'
                            }}
                        >
                            {InitialRender([customerData.fullName])}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                                <span className="customer-name" style={{ fontSize: 16 }}>{customerData.fullName}</span>
                                <span
                                    className={`customer-status-pill ${
                                        customerData.status === 'PROSPECT' ? 'status-prospect' :
                                        customerData.status === 'CALLED' ? 'status-called' :
                                        customerData.status === 'LEAD-UNCLAIMED' ? 'status-lead-unclaimed' :
                                        'status-closed-pending'
                                    }`}
                                    style={{ fontSize: 10, padding: '2px 4px', borderRadius: 2 }}
                                >
                                    {customerData.status || 'UNKNOWN'}
                                </span>
                            </div>
                            <div className="detail-row" style={{ gap: 4 }}>
                                <i className="fal fa-user detail-icon" style={{ width: 16 }}></i>
                                <span>{customerData.studentNumber}</span>
                                <CopyToClipboard data={customerData.studentNumber} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="customer-details-list" style={{ gap: 10, padding: '0px 20px' }}>
                    {/* Brand row with dot */}
                    <div className="detail-row" style={{ gap: 0 }}>
                    <span className="text-link ">
                                {brandText || (customerData.subsidiaryName ? customerData.subsidiaryName.split(':')[0].trim() : null) || (customerData.subsidiary ? String(customerData.subsidiary).split(':')[0].trim() : null) || "Academic Institute of Excellence (AIE)"}
                            </span>
                    </div>

                    {/* Programme info: Learning Method + Campus + Intake */}
                    <div className="detail-row flex-wrap" style={{ gap: 16 }}>
                        <span className={`sml-dtext ui-element cat ${learningMethodColor}`} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="fal fa-books" style={{ fontSize: 14 }}></i>
                            <span>{learningMethodText}</span>
                        </span>
                        <span className={`sml-dtext ui-element cat ${campusColor}`} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="fas fa-university" style={{ fontSize: 14 }}></i>
                            <span>{campusText}</span>
                        </span>
                        <span className="sml-dtext" style={{ color: '#495057', fontSize: 14, display: 'flex', alignItems: 'center' }}>{formattedCustomerIntake || "N/A"}</span>
                    </div>

                    {/* Divider */}
                    <hr className="w-100 my-2" style={{ margin: '8px 0' }} />

                    {/* Phone */}
                    <div className="detail-row" style={{ gap: 4 }}>
                        <i className="fal fa-phone detail-icon" style={{ width: 16 }}></i>
                        <span>{customerData.mobile}</span>
                        <CopyToClipboard data={customerData.mobile} />
                    </div>

                    {/* Email */}
                    <div className="detail-row" style={{ gap: 4 }}>
                        <i className="fal fa-envelope detail-icon" style={{ width: 16 }}></i>
                        <span style={{ maxWidth: 240, wordBreak: 'break-word' }}>{customerData.email}</span>
                        <CopyToClipboard data={customerData.email} />
                    </div>

                    {/* Location */}
                    <div className="detail-row" style={{ gap: 4 }}>
                        <i className="fal fa-map-marker-alt detail-icon" style={{ width: 16 }}></i>
                        <span style={{ maxWidth: 258 }}>{customerData.location || "-"}</span>
                    </div>

                    {/* Sales Rep */}
                    <div className="detail-row" style={{ gap: 4 }}>
                        <i className="fal fa-headset detail-icon" style={{ width: 16 }}></i>
                        <span>{customerData.salesRep || "Sales Rep"}</span>
                    </div>
                </div>

                <div className="action-button-container">
                    <button className="action-btn btn-secondary-custom" onClick={onReturnToSearch}>
                        <i className="fal fa-arrow-left"></i> Return to Search
                    </button>
                </div>
            </div>
        );
    }

    // Search / Select View
    return (
        <div className="customer-card-container">
            <div className="customer-header unknown-header">
                <div className="avatar-circle">
                    <i className="fal fa-question"></i>
                </div>
                <div>
                    <div className="customer-name">
                        {searchResults.length > 1 ? 'Multiple Matches Found' : 'Unknown'}
                    </div>
                    <div className="warning-label">
                        <i className="fas fa-exclamation-triangle"></i>
                        Select a record to continue.
                    </div>
                </div>
            </div>

            <div className="search-container">
                <label htmlFor="student-search-input">Phone / Student No. / Email</label>
                <div className="form-icon-group">
                    <input 
                        id="student-search-input"
                        type="text" 
                        className="form-control" 
                        placeholder="Search..." 
                        value={search}
                        onChange={handleSearchChange}
                    />
                    <i className="fal fa-search search-icon"></i>
                </div>

                {isSearching && (
                    <div className="mt-2 d-flex align-items-center text-muted small">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        <span>Searching...</span>
                    </div>
                )}
                
                {noRecordsFound && !isSearching && (
                    <div className="mt-3 text-center text-muted italic small">
                        There are no records to display
                    </div>
                )}

                {searchResults.length > 0 && (
                    <div
                        ref={matchesListRef}
                        className={`matches-list-container ${searchResults.length >= 4 ? 'scrollable-list' : ''} scroll-pos-${scrollPosition}`}
                        role="radiogroup"
                        aria-label="Matched students"
                        onScroll={handleMatchesScroll}
                    >
                        {searchResults.map((customer, idx) => {
                            const isSelected = isSameCustomer(selectedCustomer, customer);
                            
                            const customerKey = getCustomerId(customer) || `customer-${idx}`;
                            return (
                            <div 
                                key={customerKey} 
                                className="match-item"
                                tabIndex={0}
                                onClick={() => handleSelect(customer)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSelect(customer);
                                    }
                                    if (e.key === 'ArrowDown') {
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next) next.focus();
                                    }
                                    if (e.key === 'ArrowUp') {
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev) prev.focus();
                                    }
                                }}
                                aria-checked={isSelected}
                                role="radio"
                            >
                                <div className="match-radio">
                                    <input 
                                        type="radio" 
                                        name="customer-match"
                                        value={getCustomerId(customer) || idx}
                                        checked={isSelected}
                                        readOnly
                                        tabIndex={-1}
                                    />
                                </div>
                                <div className="match-info">
                                    <div className="match-name">{customer.fullName}</div>
                                    <div className="match-detail">
                                        <i className="fal fa-user"></i> {customer.studentNumber || customer.customerId}
                                    </div>
                                    <div className="match-detail">
                                        <i className="fal fa-envelope"></i> {customer.email}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="action-button-container">
                <button 
                    className={`action-btn ${selectedCustomer ? 'btn-primary-custom' : 'btn-disabled'}`}
                    disabled={!selectedCustomer}
                    onClick={handleLoadDetails}
                >
                    <i className="fal fa-sign-in-alt"></i> Load Student Details
                </button>
            </div>
        </div>
    );
};

export default CustomerBlock;
